import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useFormState } from 'react-final-form';
import { DocTypes, ProfileDocumentFragment, TravellerProfileFragment } from '@websky/graphql';

import { InputType } from '../../../../Passenger/types';
import { validate } from '../../../../Passenger/utils';
import { DropdownOption, TravellerDocumentFields, TravellerFields, TravellerFormType } from '../../../types';
import { DocumentFields } from '../../../consts';
import { getAvailabilityDocumentExpireDate, getCitizenships, getDocumentTypes } from './utils';

export const useDocumentFields = (
	index: number,
	validators: ProfileDocumentFragment | undefined,
	traveller: TravellerProfileFragment,
	primaryCountryCodes?: string[]
) => {
	const { t: tPassenger } = useTranslation('Passenger');

	const form = useForm();
	const formState = useFormState();

	const selectedDocType = formState.values[TravellerFields.Documents]?.[index]?.[TravellerDocumentFields.DocType];
	const selectedCitizenship =
		formState.values[TravellerFields.Documents]?.[index]?.[TravellerDocumentFields.Citizenship];

	const citizenships = getCitizenships(traveller, primaryCountryCodes);
	const documentTypes = getDocumentTypes(selectedCitizenship, validators);
	const isDocExpireDateHidden = getAvailabilityDocumentExpireDate(selectedDocType, validators);
	const isBirthRegDocument = selectedDocType === DocTypes.BirthRegDocument;

	const documentFields = [...DocumentFields];

	const requiredValidate = (value: string) => (!value ? 'Required' : undefined);

	const citizenshipValidate = requiredValidate;
	const docTypeValidate = requiredValidate;

	const docNumberValidate = (value: string, allValues: TravellerFormType) => {
		const validation = validators?.values.find(v => v.name === TravellerDocumentFields.DocNumber);

		return validate(
			value,
			{
				...(allValues[TravellerFields.Documents]?.[index] ?? {}),
				citizenships:
					formState.values[TravellerFields.Documents]?.[index]?.[TravellerDocumentFields.Citizenship]
			},
			InputType.Text,
			validation?.validationRules
		);
	};

	const docExpireDateValidate = (value: string, allValues: TravellerFormType) => {
		const validation = validators?.values.find(v => v.name === TravellerDocumentFields.DocExpiryDate);

		return validate(
			value,
			{
				...(allValues[TravellerFields.Documents]?.[index] ?? {}),
				nationality: formState.values[TravellerFields.Documents]?.[index]?.[TravellerDocumentFields.Nationality]
			},
			InputType.Date,
			validation?.validationRules
		);
	};

	const buildSaveHandler = (onSave: () => void) => () => {
		const fields = documentFields;

		if (!isDocExpireDateHidden) {
			fields.push(TravellerDocumentFields.DocExpiryDate);
		}

		fields.forEach(field => form.blur(`documents[${index}].${field}`));

		const docErrors = form.getState().errors?.[TravellerFields.Documents]?.[index];
		const hasErrors = fields.some(field => !!docErrors?.[field]);

		if (!hasErrors) {
			onSave();
		}
	};

	// Citizenships
	const sortedCitizenships = useMemo(() => {
		return [...citizenships].sort((a, b) => b.priority - a.priority);
	}, [citizenships]);

	const citizenshipsOptions = useMemo<DropdownOption[]>(() => {
		return sortedCitizenships.map(option => ({
			label: tPassenger(option.label),
			value: option.value
		}));
	}, [sortedCitizenships, tPassenger]);

	const citizenshipsByValue = useMemo(() => {
		return citizenshipsOptions.reduce((acc, curr) => {
			acc[curr.value] = curr;

			return acc;
		}, {} as Record<string, DropdownOption>);
	}, [citizenshipsOptions]);

	// Document Types
	const documentTypesOptions = useMemo<DropdownOption[]>(() => {
		return documentTypes.map(option => ({
			label: tPassenger(option),
			value: option
		}));
	}, [documentTypes, tPassenger]);

	const documentTypesByValue = useMemo(() => {
		return documentTypesOptions.reduce((acc, curr) => {
			acc[curr.value] = curr;

			return acc;
		}, {} as Record<string, DropdownOption>);
	}, [documentTypesOptions]);

	return {
		form,
		citizenshipsByValue,
		citizenshipsOptions,
		documentTypesOptions,
		documentTypesByValue,
		isDocExpireDateHidden,
		isBirthRegDocument,
		documentFields,
		citizenshipValidate,
		docTypeValidate,
		docNumberValidate,
		docExpireDateValidate,
		buildSaveHandler
	};
};
